import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api/client'

/**
 * Tenant settings: free-form jsonb, merge-update on PUT. Today's shape is
 * thin (conduct + reconciliation); the response carries whatever the
 * tenant has stored so future tunables show up automatically. The web
 * page knows about the keys it can render; everything else is preserved
 * untouched on update.
 */
export interface ConductSettings {
  contact_hours_start?: string
  contact_hours_end?: string
  contact_zone?: string
  frequency_cap_per_day?: number
  contact_working_days?: string
}

export interface ReconciliationSettings {
  auto_action_confidence_threshold?: number
  auto_action_kinds?: string
}

export interface SecuritySettings {
  /** When true, manager-role logins (TENANT_ADMIN / BRANCH_MANAGER /
   *  TEAM_LEAD) MUST have TOTP enrolled. When unset, falls back to the
   *  deploy-wide default. */
  manager_totp_required?: boolean
}

/**
 * Trip auto-approval. Absent/disabled = every request routes to the mapped
 * manager, which is the shipped default.
 */
export interface TripSettings {
  auto_approve?: {
    enabled?: boolean
    mode?: 'ALL' | 'ALLOWLIST'
    agent_emp_ids?: string[]
  }
}

/**
 * Payment gateway selection. Read carefully before changing the shape: an
 * absent or unrecognised `mode` means digital collections are REFUSED, and
 * that is deliberate rather than a gap. The backend has no default because
 * neither direction is safe — MOCK in production would report money that
 * never moved, LIVE in a test environment would charge real customers.
 */
export interface PaymentSettings {
  gateway?: {
    /** MOCK | LIVE. Absent = unconfigured = digital collections refused. */
    mode?: 'MOCK' | 'LIVE'
    provider?: string
    link_expiry_minutes?: number
  }
}

/**
 * Cash handling (V35). The ceiling is the lender's default — a manager can
 * raise it for one agent from the cash page, which is a separate record, not
 * config. `deposit_within_hours` counts only days a branch is open: Sundays
 * and the holiday calendar are skipped, and 0 switches the deadline off.
 */
export interface CashSettings {
  exposure_ceiling_inr?: string
  deposit_within_hours?: number
  deposit_banks?: DepositBank[]
}

/** One account an agent can say they deposited into. `id` is stable; the lot keeps a label snapshot. */
export interface DepositBank {
  id: string
  label: string
  bank?: string
  account_no?: string
  ifsc?: string
}

/**
 * Freshness badge thresholds, in hours since the newest DPD data. Absent =
 * the tier default (Graviton 6 / 30, Excel 30 / 54). Warn only.
 */
export interface FreshnessSettings {
  aging_hours?: number
  stale_hours?: number
}

/**
 * Graviton tier: where loans and dues are read from. No default — absent
 * means LMS sync is off. LIVE has no client yet.
 */
export interface LmsSettings {
  mode?: 'MOCK' | 'LIVE'
}

export interface BrandingSettings {
  /** Public URL to the tenant logo. Null/absent = no logo (backend does not
   *  invent a default). Keys are camelCase, matching the backend contract —
   *  intentionally inconsistent with the snake_case sections above. */
  logoUrl?: string | null
  /** Hex color, "#RRGGBB" or "#RGB". The backend always resolves this to a
   *  non-null value on GET (defaults to "#204887"), so it's only ever
   *  missing before the first fetch completes. Persisted now; rendering it
   *  into the app's actual theme is deferred to Module 18. */
  primaryColor?: string
}

/** MIS reports. Absent = 31 days, the requirement's default. */
export interface ReportSettings {
  max_range_days?: number
}

/** Printed at the foot of every receipt, one string per numbered point. */
export interface ReceiptSettings {
  terms?: string[]
}

export interface TenantSettings {
  conduct?: ConductSettings
  reconciliation?: ReconciliationSettings
  security?: SecuritySettings
  trips?: TripSettings
  payments?: PaymentSettings
  cash?: CashSettings
  freshness?: FreshnessSettings
  branding?: BrandingSettings
  lms?: LmsSettings
  receipt?: ReceiptSettings
  reports?: ReportSettings
  // Catch-all for future sections we don't render yet.
  [k: string]: unknown
}

export function useTenantSettings() {
  return useQuery({
    queryKey: ['tenant', 'settings'],
    queryFn: async () => {
      const res = await api.get<TenantSettings>('/v1/tenant/settings')
      return res.data ?? {}
    },
  })
}

/**
 * Upload the tenant logo (PNG or JPEG, at most 512 KB). The server
 * re-encodes it and points branding.logoUrl at it, so the cached settings and
 * /me are refreshed from the server rather than patched here.
 */
export function useUploadLogo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData()
      form.append('file', file)
      const res = await api.post<{ logoUrl: string }>('/v1/tenant/settings/logo', form, {
        timeout: 120_000,
      })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant', 'settings'] })
      qc.invalidateQueries({ queryKey: ['auth', 'me'] })
    },
  })
}

export function useUpdateTenantSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (patch: TenantSettings) => {
      const res = await api.put<TenantSettings>('/v1/tenant/settings', patch)
      return res.data
    },
    onSuccess: (next) => {
      qc.setQueryData(['tenant', 'settings'], next)
      // Conduct settings cascade into the case-detail banner read.
      qc.invalidateQueries({ queryKey: ['cases'] })
      qc.invalidateQueries({ queryKey: ['freshness'] })
    },
  })
}

